import {Helmet} from '@dr.pogodin/react-helmet';
import {isFeatureEnabled} from 'feature-flags';
import {Action} from 'platform/components';

import {useLocation} from 'react-router-dom';

import {featureFlags} from '@dms/feature-flags';
import i18n from '@dms/i18n';
import {businessCaseRoutes, testIds} from '@dms/routes';
import {Main, PageTab, usePermissions, usePersonnelOverviewOpen} from '@dms/shared';

import {buildArray, useNavigate} from 'shared/web';

import {BusinessCaseComplaintsList} from '../BusinessCaseComplaintsList/BusinessCaseComplaintsList';
import {BusinessCaseListOS} from '../BusinessCaseList/(sections)/BusinessCaseListOS';
import {BusinessCaseList} from '../BusinessCaseList/BusinessCaseList';
import {FinancingEndingList} from '../FinancingEndingList/FinancingEndingList';

export function BusinessCases() {
  const navigateTo = useNavigate();
  const {pathname} = useLocation();
  const [isPersonnelPanelOpen, togglePersonnelPanel] = usePersonnelOverviewOpen('business-case');

  const isBusinessCasesListTab = pathname === businessCaseRoutes.home;

  const [
    hasComplaintReadPermission,
    hasBusinessCasePurchasePermission,
    hasBusinessCaseSellingPermission,
    hasAddVehicleToBuyPermission,
    hasAddVehicleToSellPermission,
    canViewBusinessCaseSelling,
    canViewBusinessCasePurchase,
  ] = usePermissions({
    permissionKeys: [
      'complaintRead',
      'createBusinessCasePurchase',
      'createBusinessCaseSelling',
      'addVehicleToBuy',
      'addVehicleToSell',
      'viewBusinessCaseSelling',
      'viewBusinessCasePurchase',
    ],
    scopes: {
      complaintRead: {
        // Skipping both scopes, BE handles DG ACL
        participation: 'SKIP',
        // Skipping both scopes, BE handles DG ACL
        branchId: 'SKIP',
      },
      viewBusinessCasePurchase: {
        // Skipping both scopes, BE handles DG ACL
        participation: 'SKIP',
        // Skipping both scopes, BE handles DG ACL
        branchId: 'SKIP',
      },
      viewBusinessCaseSelling: {
        // Skipping both scopes, BE handles DG ACL
        participation: 'SKIP',
        // Skipping both scopes, BE handles DG ACL
        branchId: 'SKIP',
      },
    },
  });

  const canCreatePurchase = hasBusinessCasePurchasePermission && hasAddVehicleToBuyPermission;
  const canCreateSell = hasBusinessCaseSellingPermission && hasAddVehicleToSellPermission;
  const canCreateBusinessCase =
    (canCreatePurchase && canViewBusinessCasePurchase) ||
    (canCreateSell && canViewBusinessCaseSelling);
  const canViewBusinessCase = canViewBusinessCasePurchase || canViewBusinessCaseSelling;

  const handleClickNewConcept = () => {
    navigateTo(businessCaseRoutes.concept);
  };

  const tabs = buildArray<PageTab>()
    .add({
      id: 'business-cases-list',
      title: i18n.t('page.businessCases.title'),
      href: businessCaseRoutes.home,
      content: (
        <BusinessCaseList
          isPersonnelPanelOpen={isPersonnelPanelOpen}
          canViewBusinessCase={canViewBusinessCase}
        />
      ),
    })
    .when(hasComplaintReadPermission, {
      id: 'business-cases-complaints-list',
      title: i18n.t('entity.vehicle.labels.complaints'),
      href: businessCaseRoutes.complaintsList,
      content: <BusinessCaseComplaintsList />,
    })
    .whenFeatureEnabled(featureFlags.OS_BUSINESS_CASES, {
      id: 'business-cases-list-open-search',
      title: i18n.t('page.businessCases.360businessCasesView.title'),
      href: businessCaseRoutes.openSearch,
      content: <BusinessCaseListOS />,
    })
    .when(canViewBusinessCaseSelling && isFeatureEnabled(featureFlags.SALES_FINANCING_ENDING), {
      id: 'business-cases-financing-ending',
      title: i18n.t('page.businessCases.financingEnding.title'),
      href: businessCaseRoutes.financingEnding,
      content: <FinancingEndingList />,
    });

  const actions = buildArray<Action>([])
    .when(canViewBusinessCase && isBusinessCasesListTab, {
      type: 'iconButtonWithTooltip',
      icon: 'action/vertical_split',
      priority: isPersonnelPanelOpen ? 'info' : 'secondary',
      onClick: togglePersonnelPanel,
      tooltip: {label: i18n.t('page.personnelOverview.title')},
      'data-testid': testIds.businessCase.home('togglePersonnelPanel'),
    })
    .when(canCreateBusinessCase, {
      type: 'button',
      variant: 'primary',
      onClick: handleClickNewConcept,
      'data-testid': testIds.businessCase.home('createNew'),
      title: i18n.t('entity.businessCase.createNew'),
    });

  return (
    <>
      <Helmet title={i18n.t('page.businessCases.title')} />
      <Main actions={actions} tabs={tabs} isFullHeight />
    </>
  );
}
